import type manifest from '@lansforsakringar/icons/manifest.svg.json'
import type { TemplateResult } from 'lit'

import { styleMap } from 'lit/directives/style-map.js'
import icons from 'virtual:@lansforsakringar/icons'
import { state, property } from 'lit/decorators.js'
import { html, nothing } from 'lit'

import { LfuiElement } from '#utils/element'

import styles, { locals } from './lfds-icon.css.ts'

const cache = new WeakMap()

type ExtractIconName<T extends string> = T extends `${infer Base}-${number}`
	? Base
	: never

/**
 * @name LfdsIcon
 *
 * @status ready
 * @tag lfds-icon
 */
export default class LfdsIcon extends LfuiElement {
	static styles = styles

	static preload(id: string, size: number) {
		const load = icons[`${id}-${size}`]
		if (load && !cache.has(load)) {
			load().then((m) => {
				cache.set(load, m.default)
			})
		}
	}

	@property({ attribute: 'icon-id' }) iconId: ExtractIconName<
		keyof typeof manifest
	> = 'placeholder'

	@property({ type: Number }) size:
		| 12
		| 16
		| 22
		| 24
		| 28
		| 32
		| 40
		| 48
		| 64
		| 72
		| 96 = 24

	@property() color?: string

	@state() private icon?: () => TemplateResult

	willUpdate(changed) {
		super.willUpdate(changed)

		if (changed.has('iconId') || changed.has('size')) {
			const load = icons[`${this.iconId}-${this.size}`]
			if (load) {
				if (cache.has(load)) {
					this.icon = cache.get(load)
				} else {
					load().then((m) => {
						this.icon = m.default
						cache.set(load, m.default)
					})
				}
			} else {
				console.warn(`Icon ${this.iconId} not found for size ${this.size}`)
				this.icon = undefined
			}
		}
	}

	render() {
		if (!this.icon) return nothing

		return html`<span
			class="icon"
			aria-hidden="true"
			style="${styleMap({
				[locals.internal_size]: String(this.size),
				[locals.color]: this.color
			})}"
		>
			${this.icon()}
		</span>`
	}
}
